(() => {
  const roots = document.querySelectorAll('[data-pb-welcome-popup]');
  if (!roots.length) return;

  const url = new URL(window.location.href);
  const queryPreview = url.searchParams.get('pb_welcome_preview') === '1';

  roots.forEach((root) => {
    if (root.dataset.pbWelcomeReady === 'true') return;
    root.dataset.pbWelcomeReady = 'true';

    const configured = root.dataset.enabled === 'true' && root.dataset.integrationReady === 'true';
    const designMode = root.dataset.designMode === 'true';
    const previewOnly = !configured && (queryPreview || designMode);
    if (!configured && !previewOnly) return;

    const dialog = root.querySelector('[data-pb-welcome-dialog]');
    const trigger = root.querySelector('[data-pb-welcome-trigger]');
    const composition = root.querySelector('.pb-welcome-popup__composition');
    const form = root.querySelector('form');
    const email = root.querySelector('[data-pb-welcome-email]');
    const error = root.querySelector('[data-pb-welcome-error]');
    const previewNote = root.querySelector('[data-pb-welcome-preview-note]');
    const submit = root.querySelector('[data-pb-welcome-submit]');
    const submitLabel = root.querySelector('[data-pb-welcome-submit-label]');
    const closeButton = root.querySelector('[data-pb-welcome-close]');
    const initialSubmitLabel = submitLabel?.textContent || '';
    const isGerman = document.documentElement.lang.toLowerCase().startsWith('de');
    const isDutch = document.documentElement.lang.toLowerCase().startsWith('nl');
    const storageKey = 'pb-welcome-popup-state-v1';
    const autoOpen = root.dataset.autoOpen === 'true';
    const autoDelayMs = Math.max(5, Number(root.dataset.autoDelaySeconds) || 45) * 1000;
    // C7 WP7: count page views in this visit; the offer opens on the second page (after a short pause)
    // or after autoDelayMs on the first page. Scroll depth no longer opens it.
    const pageviewKey = 'pb-welcome-pageviews-v1';
    let pageviews = 1;
    try {
      pageviews = (Number(window.sessionStorage.getItem(pageviewKey)) || 0) + 1;
      window.sessionStorage.setItem(pageviewKey, String(pageviews));
    } catch (error) {
      pageviews = 1;
    }
    const autoScrollPercent = Math.min(100, Math.max(10, Number(root.dataset.autoScrollPercent) || 30));
    const dismissMs = Math.max(30, Number(root.dataset.dismissDays) || 90) * 86400000;
    const excludedPath = /^(?:\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?(?:cart|checkout|account)(?:\/|$)|\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?policies(?:\/|$)|\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?pages\/(?:contact|contact-us|data-sharing-opt-out)(?:\/|$)|\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?password(?:\/|$))/i.test(window.location.pathname);
    const embeddedScroller = document.querySelector('.page-wrapper');
    const scrollTarget = embeddedScroller && /(auto|scroll)/.test(window.getComputedStyle(embeddedScroller).overflowY)
      ? embeddedScroller
      : window;
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    const requestFrame = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
    let phase = 'closed';
    let autoHandled = false;
    let autoRetryTimer;
    let closeTimer;
    let teaserPositionFrame;
    let backdropPointerDown;

    if (!dialog || !trigger) return;
    root.hidden = false;
    root.dataset.previewOnly = String(previewOnly);
    root.dataset.phase = phase;

    const readState = () => {
      try {
        return JSON.parse(window.localStorage.getItem(storageKey) || 'null');
      } catch (_error) {
        return null;
      }
    };

    const writeState = (status) => {
      try {
        window.localStorage.setItem(storageKey, JSON.stringify({ status, at: Date.now() }));
      } catch (_error) {
        // Storage is optional; the current page still behaves correctly.
      }
    };

    const hasSubmittedState = () => {
      const status = readState()?.status;
      return status === 'submitted' || status === 'confirmed' || status === 'form_posted';
    };

    const automaticOpeningIsSuppressed = () => {
      if (queryPreview) return false;
      const state = readState();
      if (!state || !state.status) return false;
      if (state.status === 'submitted' || state.status === 'confirmed' || state.status === 'form_posted') return true;
      return state.status === 'dismissed' && Number(state.at) + dismissMs > Date.now();
    };

    const otherDialogIsOpen = () =>
      Array.from(document.querySelectorAll('dialog[open], [role="dialog"], [role="alertdialog"], [aria-modal="true"]'))
        .some((candidate) =>
          !root.contains(candidate) &&
          candidate.getAttribute('aria-hidden') !== 'true' &&
          !candidate.hidden &&
          candidate.getClientRects().length > 0 &&
          window.getComputedStyle(candidate).visibility !== 'hidden'
        );

    const syncTeaserPosition = () => {
      teaserPositionFrame = undefined;
      if (trigger.hidden) return;

      const viewportHeight = window.innerHeight;
      const stickyBar = Array.from(document.querySelectorAll('sticky-add-to-cart [ref="stickyBar"]'))
        .find((bar) => {
          const rect = bar.getBoundingClientRect();
          const styles = window.getComputedStyle(bar);
          return bar.dataset.stuck === 'true' &&
            rect.width > 0 &&
            rect.height > 0 &&
            rect.top < viewportHeight &&
            rect.bottom > 0 &&
            styles.visibility !== 'hidden' &&
            styles.display !== 'none';
        });
      const lift = stickyBar
        ? Math.max(0, viewportHeight - stickyBar.getBoundingClientRect().top + 12)
        : 0;
      root.style.setProperty('--pb-welcome-teaser-lift', `${Math.ceil(lift)}px`);
    };

    const scheduleTeaserPosition = () => {
      if (teaserPositionFrame !== undefined) return;
      teaserPositionFrame = requestFrame(syncTeaserPosition);
    };

    const syncTriggerState = (open) => {
      trigger.setAttribute('aria-expanded', String(open));
    };

    const showTrigger = (focus = false) => {
      trigger.classList.remove('pb-welcome-popup__teaser--returning');
      trigger.removeAttribute('aria-hidden');
      trigger.removeAttribute('tabindex');
      if (hasSubmittedState() && !queryPreview) {
        trigger.hidden = true;
        syncTriggerState(false);
        return false;
      }
      trigger.hidden = false;
      scheduleTeaserPosition();
      if (focus) trigger.focus({ preventScroll: true });
      return true;
    };

    const finishClose = () => {
      window.clearTimeout(closeTimer);
      closeTimer = undefined;
      dialog.classList.remove('is-closing');
      dialog.style.removeProperty('--pb-welcome-exit-x');
      dialog.style.removeProperty('--pb-welcome-exit-y');
      dialog.style.removeProperty('--pb-welcome-exit-start-opacity');
      dialog.style.removeProperty('--pb-welcome-exit-start-transform');
      if (dialog.open) dialog.close();
      phase = 'closed';
      root.dataset.phase = phase;
      syncTriggerState(false);
      showTrigger(true);
    };

    const interruptClosing = () => {
      if (phase !== 'closing') return;
      window.clearTimeout(closeTimer);
      closeTimer = undefined;
      dialog.classList.remove('is-closing');
      dialog.style.removeProperty('--pb-welcome-exit-x');
      dialog.style.removeProperty('--pb-welcome-exit-y');
      dialog.style.removeProperty('--pb-welcome-exit-start-opacity');
      dialog.style.removeProperty('--pb-welcome-exit-start-transform');
      trigger.hidden = true;
      trigger.classList.remove('pb-welcome-popup__teaser--returning');
      trigger.removeAttribute('aria-hidden');
      trigger.removeAttribute('tabindex');
      phase = 'open';
      root.dataset.phase = phase;
      syncTriggerState(true);
      closeButton?.focus({ preventScroll: true });
    };

    const close = ({ suppressAuto = true } = {}) => {
      if (!dialog.open || phase === 'closing') return false;
      phase = 'closing';
      root.dataset.phase = phase;
      autoHandled = true;
      window.clearTimeout(autoRetryTimer);
      if (suppressAuto && !queryPreview && !hasSubmittedState()) writeState('dismissed');

      if (reducedMotion) {
        finishClose();
        return true;
      }

      trigger.hidden = false;
      trigger.setAttribute('aria-hidden', 'true');
      trigger.setAttribute('tabindex', '-1');
      syncTeaserPosition();

      const dialogRect = dialog.getBoundingClientRect();
      const triggerRect = trigger.getBoundingClientRect();
      const dialogStyles = window.getComputedStyle(dialog);
      const dialogCenterX = dialogRect.left + (dialogRect.width / 2);
      const dialogCenterY = dialogRect.top + (dialogRect.height / 2);
      const triggerCenterX = triggerRect.left + (triggerRect.width / 2);
      const triggerCenterY = triggerRect.top + (triggerRect.height / 2);
      dialog.style.setProperty('--pb-welcome-exit-x', `${Math.round(triggerCenterX - dialogCenterX)}px`);
      dialog.style.setProperty('--pb-welcome-exit-y', `${Math.round(triggerCenterY - dialogCenterY)}px`);
      dialog.style.setProperty('--pb-welcome-exit-start-opacity', dialogStyles.opacity);
      dialog.style.setProperty('--pb-welcome-exit-start-transform', dialogStyles.transform === 'none' ? 'none' : dialogStyles.transform);
      dialog.classList.add('is-closing');

      trigger.classList.add('pb-welcome-popup__teaser--returning');
      closeTimer = window.setTimeout(finishClose, 340);
      return true;
    };

    const open = (reason = 'manual') => {
      if (phase === 'closing') interruptClosing();
      if (phase !== 'closed' || dialog.open || otherDialogIsOpen() || document.visibilityState === 'hidden') return false;
      trigger.hidden = true;
      trigger.classList.remove('pb-welcome-popup__teaser--returning');
      trigger.removeAttribute('aria-hidden');
      trigger.removeAttribute('tabindex');
      root.dataset.openReason = reason;
      phase = 'opening';
      root.dataset.phase = phase;
      syncTriggerState(true);
      dialog.showModal();
      closeButton?.focus({ preventScroll: true });
      if (reducedMotion) {
        phase = 'open';
        root.dataset.phase = phase;
      } else {
        requestFrame(() => {
          if (phase === 'opening') {
            phase = 'open';
            root.dataset.phase = phase;
          }
        });
      }
      return true;
    };

    const requestAutomaticOpen = (reason, attemptsLeft = 20) => {
      if (autoHandled || automaticOpeningIsSuppressed()) return;
      if (open(reason)) {
        autoHandled = true;
        scrollTarget.removeEventListener('scroll', onScroll);
        return;
      }
      if (attemptsLeft > 0) {
        window.clearTimeout(autoRetryTimer);
        autoRetryTimer = window.setTimeout(() => requestAutomaticOpen(reason, attemptsLeft - 1), 1500);
      }
    };

    const onScroll = () => {
      const scrollable = scrollTarget === window
        ? Math.max(1, document.documentElement.scrollHeight - window.innerHeight)
        : Math.max(1, scrollTarget.scrollHeight - scrollTarget.clientHeight);
      const position = scrollTarget === window ? window.scrollY : scrollTarget.scrollTop;
      const progress = (position / scrollable) * 100;
      if (progress >= autoScrollPercent) requestAutomaticOpen('scroll');
    };

    const keepFieldReachable = () => {
      if (!dialog.open || !composition) return;
      requestFrame(() => {
        const viewport = window.visualViewport;
        const viewportTop = viewport?.offsetTop || 0;
        const viewportBottom = viewportTop + (viewport?.height || window.innerHeight);
        const active = dialog.contains(document.activeElement) && document.activeElement !== dialog
          ? document.activeElement
          : dialog.querySelector('[data-pb-welcome-email]');
        if (!active?.getBoundingClientRect) return;
        const rect = active.getBoundingClientRect();
        const maxScrollTop = Math.max(0, composition.scrollHeight - composition.clientHeight);
        if (rect.bottom > viewportBottom - 16) {
          composition.scrollTop = Math.min(maxScrollTop, composition.scrollTop + rect.bottom - (viewportBottom - 16));
        } else if (rect.top < viewportTop + 16) {
          composition.scrollTop = Math.max(0, composition.scrollTop - ((viewportTop + 16) - rect.top));
        }
      });
    };

    const syncDialogViewport = () => {
      const viewport = window.visualViewport;
      const layoutHeight = Math.max(1, window.innerHeight || document.documentElement.clientHeight || 1);
      const viewportHeight = Math.max(1, viewport?.height || layoutHeight);
      const viewportOffsetTop = Math.max(0, viewport?.offsetTop || 0);
      root.style.setProperty('--pb-welcome-layout-height', `${Math.round(layoutHeight)}px`);
      root.style.setProperty('--pb-welcome-viewport-height', `${Math.round(viewportHeight)}px`);
      root.style.setProperty('--pb-welcome-viewport-offset-top', `${Math.round(viewportOffsetTop)}px`);
      if (dialog.open) keepFieldReachable();
    };

    trigger.addEventListener('click', () => open('manual'));
    root.querySelectorAll('[data-pb-welcome-close]').forEach((button) => {
      button.addEventListener('click', () => close());
    });
    root.querySelector('[data-pb-welcome-dismiss]')?.addEventListener('click', () => close());

    const pointerIsOutsideDialog = (event) => {
      const rect = dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    };

    dialog.addEventListener('pointerdown', (event) => {
      backdropPointerDown = event.target === dialog && pointerIsOutsideDialog(event)
        ? { pointerId: event.pointerId }
        : undefined;
    });

    dialog.addEventListener('pointerup', (event) => {
      const shouldClose = backdropPointerDown?.pointerId === event.pointerId &&
        event.target === dialog &&
        pointerIsOutsideDialog(event);
      backdropPointerDown = undefined;
      if (shouldClose) close();
    });

    dialog.addEventListener('pointercancel', () => {
      backdropPointerDown = undefined;
    });

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });

    form?.addEventListener('submit', (event) => {
      if (!email || !submit) return;
      email.value = email.value.trim();
      if (!email.value || !email.validity.valid) {
        event.preventDefault();
        email.setAttribute('aria-invalid', 'true');
        if (error) {
          error.textContent = isGerman
            ? (email.value ? 'Bitte gib eine gültige E-Mail-Adresse ein.' : 'Bitte gib deine E-Mail-Adresse ein.')
            : isDutch
              ? (email.value ? 'Vul een geldig e-mailadres in.' : 'Vul je e-mailadres in.')
              : (email.value ? 'Please enter a valid email address.' : 'Please enter your email address.');
        }
        email.focus();
        keepFieldReachable();
        return;
      }

      email.setAttribute('aria-invalid', 'false');
      if (error) error.textContent = '';

      if (previewOnly) {
        event.preventDefault();
        if (previewNote) {
          previewNote.hidden = false;
          previewNote.textContent = root.dataset.previewMessage;
          previewNote.focus?.({ preventScroll: true });
        }
        return;
      }

      submit.disabled = true;
      form.setAttribute('aria-busy', 'true');
      if (submitLabel) submitLabel.textContent = isGerman ? 'Wird übermittelt…' : isDutch ? 'Verzenden…' : 'Submitting…';
    });

    email?.addEventListener('focus', keepFieldReachable);
    email?.addEventListener('input', () => {
      email.setAttribute('aria-invalid', 'false');
      if (error) error.textContent = '';
      if (previewNote) previewNote.hidden = true;
      if (submit) submit.disabled = false;
      form?.removeAttribute('aria-busy');
      if (submitLabel) submitLabel.textContent = initialSubmitLabel;
    });

    window.addEventListener('resize', () => {
      syncDialogViewport();
      scheduleTeaserPosition();
    }, { passive: true });
    window.addEventListener('scroll', scheduleTeaserPosition, { passive: true });
    if (scrollTarget !== window) scrollTarget.addEventListener('scroll', scheduleTeaserPosition, { passive: true });

    // Phones: tuck the teaser away while the shopper scrolls down, bring it back on scroll up.
    const tuckQuery = window.matchMedia('(max-width: 749px)');
    const scrollPosition = () => (scrollTarget === window ? window.scrollY : scrollTarget.scrollTop);
    let lastScrollPosition = scrollPosition();
    const syncTeaserTuck = () => {
      const position = scrollPosition();
      const delta = position - lastScrollPosition;
      if (Math.abs(delta) < 8) return;
      trigger.classList.toggle('is-tucked', tuckQuery.matches && delta > 0 && position > 120);
      lastScrollPosition = position;
    };
    (scrollTarget === window ? window : scrollTarget).addEventListener('scroll', syncTeaserTuck, { passive: true });
    window.visualViewport?.addEventListener('resize', () => {
      syncDialogViewport();
      scheduleTeaserPosition();
    }, { passive: true });
    syncDialogViewport();
    scheduleTeaserPosition();

    const hasServerState = root.querySelector('[data-pb-welcome-state="pending"]') || Boolean(error?.textContent.trim());
    if (root.querySelector('[data-pb-welcome-state="pending"]')) writeState('submitted');
    if (hasServerState && configured) {
      open('server-state');
      return;
    }

    if (queryPreview) {
      window.setTimeout(() => requestAutomaticOpen('preview'), 650);
      return;
    }

    if (hasSubmittedState()) trigger.hidden = true;
    // Keep the manual offer available without interrupting product selection.
    const isProductPath = /\/products(?:\/|$)/i.test(window.location.pathname);
    if (!configured || !autoOpen || excludedPath || isProductPath || automaticOpeningIsSuppressed()) return;

    if (pageviews >= 2) {
      window.setTimeout(() => requestAutomaticOpen('second-page'), 3000);
    } else {
      window.setTimeout(() => requestAutomaticOpen('delay'), autoDelayMs);
    }
  });
})();
